import type { SignupAttribution } from '@property/shared';
import { api } from './api';

/**
 * First-touch attribution capture.
 *
 * WorkOS AuthKit is a hosted, redirect-based flow, so the marketing URL (and
 * its utm params) is gone by the time auth completes. We capture the params on
 * the visitor's very first landing, stash them in localStorage so they survive
 * the redirect round-trip, then POST them once after the user is authenticated.
 */

const STORAGE_KEY = 'boundr.attribution';
const SYNCED_KEY = 'boundr.attribution.synced';

/**
 * Capture attribution from the current URL, first-touch wins. Safe to call on
 * every app load — it no-ops once something has been stored. Always records a
 * row (referrer + landing path) so organic signups are countable too.
 */
export function captureAttribution(): void {
  if (typeof window === 'undefined') return;

  try {
    if (localStorage.getItem(STORAGE_KEY)) return; // first-touch already captured

    const params = new URLSearchParams(window.location.search);
    const get = (key: string) => params.get(key)?.trim() || undefined;

    const data: SignupAttribution = {
      utmSource: get('utm_source'),
      utmMedium: get('utm_medium'),
      utmCampaign: get('utm_campaign'),
      utmTerm: get('utm_term'),
      utmContent: get('utm_content'),
      gclid: get('gclid'),
      referrer: document.referrer || undefined,
      landingPath: window.location.pathname + window.location.search,
      // Set by GET /r/:code (infra/lib/constructs/static-site.ts routes /r/*
      // to the API), which resolves the referral code to the referring user
      // before redirecting here.
      referredByUserId: get('ref_uid'),
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // localStorage can throw (private mode / quota) — attribution is best-effort
  }
}

/**
 * Whether a captured payload carries an explicit campaign signal worth a write.
 * Only utm_* / gclid count — `referrer` is excluded on purpose, since almost
 * every external visit sets document.referrer and would make this always true.
 */
function hasAttributionSignal(data: SignupAttribution): boolean {
  return Boolean(
    data.utmSource ||
      data.utmMedium ||
      data.utmCampaign ||
      data.utmTerm ||
      data.utmContent ||
      data.gclid,
  );
}

/**
 * Return the stored attribution payload for inclusion as PostHog event
 * properties. Safe to call before the user has signed up — returns {} if
 * nothing has been captured yet.
 */
export function getAttributionProps(): Partial<SignupAttribution> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

/**
 * Send the captured attribution to the API once. Backend is idempotent
 * (first-write-wins per user), and we flag a local "synced" marker to avoid a
 * request on every page load. Organic visits (no utm/gclid) are skipped
 * entirely so we don't spend a Lambda invocation recording "no source".
 * Failures are swallowed and retried next load.
 */
export async function syncAttribution(): Promise<void> {
  if (typeof window === 'undefined') return;

  try {
    if (localStorage.getItem(SYNCED_KEY)) return;

    const raw = localStorage.getItem(STORAGE_KEY);
    const data: SignupAttribution = raw ? JSON.parse(raw) : {};

    // Organic / direct: nothing to attribute. Mark synced so we never retry
    // and never hit the API for this user on this browser.
    if (!hasAttributionSignal(data)) {
      localStorage.setItem(SYNCED_KEY, '1');
      localStorage.removeItem(STORAGE_KEY);
      return;
    }

    await api.recordAttribution(data);
    // Data is now in the DB. Remove the local copy (it was only needed to
    // survive the WorkOS redirect) and mark synced to prevent re-firing.
    localStorage.removeItem(STORAGE_KEY);
    localStorage.setItem(SYNCED_KEY, '1');
  } catch {
    // Leave both flags untouched so the data and the retry opportunity survive.
  }
}
